import { useState, useRef } from "react";
import { GitBranch, Home, Plus, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface BranchComboboxProps {
  value: string;
  onChange: (value: string) => void;
  userBranches?: string[]; // User's existing branches (excluding main)
  className?: string;
}

function validateBranchName(name: string): boolean {
  // Branch name validation: lowercase letters, numbers, hyphens
  return /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(name);
}

export default function BranchCombobox({
  value,
  onChange,
  userBranches = [],
  className,
}: BranchComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (newValue: string) => {
    // Convert to lowercase and replace spaces with hyphens
    const normalized = newValue.toLowerCase().replace(/\s+/g, "-");
    setInputValue(normalized);
    // Update form value in real-time for live preview
    if (normalized && validateBranchName(normalized)) {
      onChange(normalized);
    }
  };

  const handleInputBlur = () => {
    // Validate and apply on blur
    if (inputValue && validateBranchName(inputValue)) {
      onChange(inputValue);
    } else if (!inputValue) {
      onChange("main");
      setInputValue("main");
    } else {
      setInputValue(value);
    }
  };

  const handleSelectBranch = (branchName: string) => {
    setInputValue(branchName);
    onChange(branchName);
    setIsOpen(false);
  };

  const isMain = inputValue === "main";

  return (
    <div className={cn("relative flex", className)}>
      <div className="relative flex-1">
        {isMain ? (
          <Home className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
        ) : (
          <GitBranch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
        )}
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onBlur={handleInputBlur}
          placeholder="main"
          className="pl-9 pr-8 font-mono"
        />
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full w-8 rounded-l-none"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[200px]">
            {/* Main branch - always shown */}
            <DropdownMenuItem
              onClick={() => handleSelectBranch("main")}
              className={cn(
                "font-mono",
                value === "main" && "bg-[hsl(var(--accent))]"
              )}
            >
              <Home className="mr-2 h-4 w-4" />
              main
              <span className="ml-auto text-xs text-[hsl(var(--muted-foreground))]">
                Default
              </span>
            </DropdownMenuItem>

            {/* User's existing branches */}
            {userBranches.length > 0 && (
              <>
                <DropdownMenuSeparator />
                {userBranches.map((branch) => (
                  <DropdownMenuItem
                    key={branch}
                    onClick={() => handleSelectBranch(branch)}
                    className={cn(
                      "font-mono",
                      value === branch && "bg-[hsl(var(--accent))]"
                    )}
                  >
                    <GitBranch className="mr-2 h-4 w-4" />
                    {branch}
                  </DropdownMenuItem>
                ))}
              </>
            )}

            {/* New branch option - always shown */}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                setInputValue("");
                setIsOpen(false);
                // Use multiple rAF to wait for Radix focus management to complete
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    inputRef.current?.focus();
                  });
                });
              }}
              className="font-mono text-[hsl(var(--primary))]"
            >
              <Plus className="mr-2 h-4 w-4" />
              New branch
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
